import type { WithRequiredTheme } from '@opallabs/lapidary'
import type { PasswordRules } from '@opallabs/principles'

export const enum SsoStatus {
  /**
   * SSO is completely disabled.
   */
  Disabled = 'disabled',
  /**
   * SSO is enabled; users can log in using any method the system supports
   * (email + password, magic links, SSO).
   */
  Enabled = 'enabled',
  /**
   * ‘Agency mode’: if a given user account is configured for SSO (that is,
   * the opal has a valid, active SSO configuration and the user account has
   * associated SSO information in the database), the SSO status is `required`;
   * for user accounts that aren’t configured for SSO the SSO status is
   * `enabled`.
   *
   * The ‘agency mode’ nickname helps explain the motivation: customers often
   * provide user accounts to the marketing and creative agencies they work
   * with. These accounts generally don’t have records in the customer’s SSO
   * system, so they inherently can’t use SSO. But the customer wants their own
   * employees, who *do* have records in the customer’s SSO system, to be
   * required to log in with SSO. The `enabled_exclusive` mode supports this by
   * assuming all accounts are `enabled` by default, then flipping to
   * `required` if the user has ever logged in with SSO.
   */
  EnabledExclusive = 'enabled_exclusive',
  /**
   * Users can only log in using SSO.
   */
  Required = 'required',
}

export type AccountCreationProps = Readonly<
  WithRequiredTheme & {
    companyName?: string
    currentAvatarUuid?: string
    currentEmailAddress?: string
    currentFamilyName?: string
    currentGivenName?: string
    currentJobTitle?: string
    exampleFamilyName?: string
    exampleGivenName?: string
    passwordRules?: PasswordRules
    ssoIdpName?: string
    /**
     * @default SsoStatus.Disabled
     */
    ssoStatus: SsoStatus
    /**
     * @default ""
     */
    ssoUrl: string
    tosAccepted?: boolean
    userId?: number
  }
>

export type StyleProps = WithRequiredTheme
